import { Component, OnInit, AfterViewInit, ViewChild, Input, ViewChildren, QueryList } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { ThirdPartyTracingActionListItem } from '../../../../models/thirdparty-tracingaction-listitem';
import { ThirdPartyEvaluationResult } from '../../../../models/thirdparty-evaluation-result';
import { LockDialogComponent } from './dialogs/lock-dialog/lock-dialog.component';
import { AddtaskDialogComponent } from './dialogs/addtask-dialog/addtask-dialog.component';
import { Guid } from 'guid-typescript';
import { ActivatedRoute, Router } from '@angular/router';
import { ThirdPartyEvaluationService } from '../../../../services/thirdparty-evaluation.service';
import { NotificationService } from '../../../../services/notification.service';
import { ThirdPartyEvaluationConfirmation } from '../../../../models/thirdparty-evaluation-confirmation';
import { BehaviorSubject } from 'rxjs';
import { ThirdPartyEvaluationStatus } from '../../../../models/thirdparty-evaluation-status';
import { ThirdpartiesService } from '../../../../services/thirdparties.service';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../../core/auth/_services/auth.service';
import { DueDiligenceService } from '../../../../services/due-diligence.service';
import { ValidateBlankSpaces } from './customValidators/evaluation-customValidator';
import { SpecialTaskDetailDialogComponent } from './dialogs/special-task-detail-dialog/special-task-detail-dialog.component';
import { ConfirmationDialogComponent } from '../../../models/common-dialogs/confirmation-dialog/confirmation-dialog.component';
import { TracingPlanTaskComponent } from '../tracing-plan-task/tracing-plan-task.component';
import { IterativeTaskComponent } from '../iterative-task/iterative-task.component';
import { FollowUpResponseEnum } from '../../../../models/tracing-plan-task-item';
import { PermissionType } from '@app/core/auth/enums/permission.enum';

enum NextEvaluationAction {
  NextWorkFlowStep = 1,
  ApproveWithTracing = 2,
  Reject = 3,
  SkipNextWorkFlowStageAndRegenarate = 4,
  ByPassNextWorkFlowStage = 5,
};

@Component({
  selector: 'kt-evaluation-score-page',
  templateUrl: './evaluation-score-page.component.html',
  styleUrls: ['./evaluation-score-page.component.scss']
})
export class EvaluationScorePageComponent implements OnInit, AfterViewInit {
  pageLoading$: BehaviorSubject<boolean>;
  @Input() review = false;

  recommendedActionsDataSource: MatTableDataSource<ThirdPartyTracingActionListItem>;
  specialActionsDataSource: MatTableDataSource<ThirdPartyTracingActionListItem>;
  optionalActionsDataSource: MatTableDataSource<ThirdPartyTracingActionListItem>;

  customActionsDataSource: MatTableDataSource<ThirdPartyTracingActionListItem>;
  prevCustomActionsDataSource: MatTableDataSource<ThirdPartyTracingActionListItem>;

  displayedColumnsRecommended: string[] = [
    'recommended',
    'tracingaction',
    'responsableRole',
    'use_last_plan',
    'reset_expiration',
    'view_detail',
  ];

  displayedColumnsSpecial: string[] = [
    'recommended',
    'tracingaction',
    'responsableRole',
    'use_last_plan',
    'reset_expiration',
    'actions',
  ];

  optionalColumns: string[] = [
    'recommended',
    'tracingaction',
    'responsableRole',
    'use_last_plan',
    'reset_expiration',
    'view_detail',
    // 'actions'
  ];

  displayedPrevColumnsCustom: string[] = [
    'recommended',
    'tracingaction',
    'expirationDate',
    'responsableRole',
    'use_last_plan',
    'reset_expiration',
    'view_detail',
  ];

  displayedColumnsCustom: string[] = [
    'tracingaction',
    'expirationDate',
    'responsableRole',
    'actions',
  ];

  editLocked$: BehaviorSubject<boolean>;

  evaluationResult$: BehaviorSubject<ThirdPartyEvaluationResult>;
  thirdPartyName: string = '';

  nextEvaluationAction = NextEvaluationAction;
  nextAction: NextEvaluationAction;

  // Dyamic reference to table (static: false)
  @ViewChild('customTracingActionTable') customTracingActionTable: MatTable<any>;

  scoreResultFormGroup: FormGroup;
  maxInputLength: number = 255;
  minInputLength: number = 10;
  userChangesReason: string = '';

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private _thirdpartyService: ThirdpartiesService,
    private _evaluationService: ThirdPartyEvaluationService,
    private _notificationService: NotificationService,
    private fb: FormBuilder,
    private _translateService: TranslateService,
    private _authService: AuthService,
    private _dueDiligenceService: DueDiligenceService
  ) {
    this.editLocked$ = new BehaviorSubject<boolean>(true);
    this.evaluationResult$ = new BehaviorSubject<ThirdPartyEvaluationResult>(new ThirdPartyEvaluationResult());
    this.recommendedActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>();
    this.specialActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>();
    this.customActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>();
    this.prevCustomActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>();
    this.optionalActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>();

    this.pageLoading$ = new BehaviorSubject<boolean>(true);
  }

  ngOnInit() {
    var thirdPartyEvaluationId = this.route.snapshot.paramMap.get("id");
    // Stop loading animation
    this.evaluationResult$.subscribe(x => {
      if (x.thridPartyId) {
        this._thirdpartyService.getDetail(x.thridPartyId).subscribe({
          next: res => this.thirdPartyName = res.name,
          complete: () => this.pageLoading$.next(false)
        });
      }
    });

    if (this.review) {
      this.displayedColumnsRecommended.unshift('reviewed');
      this.displayedColumnsSpecial.unshift('reviewed');
      // this.displayedPrevColumnsCustom.unshift('reviewed');
      this.optionalColumns.unshift('reviewed');

      // this.displayedPrevColumnsCustom = this.optionalColumns.filter(x => x !== 'recommended');
      this.optionalColumns = this.optionalColumns.filter(x => x !== 'recommended');

      this._evaluationService.getEvaluationApprovementRequest(thirdPartyEvaluationId).subscribe(res => {
        this.evaluationResult$.next(res.suggestionResultDto);
        this.nextAction = res.nextStepSelectedByUser;

        this.userChangesReason = res.evaluationStateDto.formsCompleted[res.evaluationStateDto.formsCompleted.length - 1].observation;
        this.createFormGroup(this.editLocked$.getValue());

        // Acciones propuestas por el sistemas seleccionadas
        const selectedDefaultActionIds = res.taskDtos.filter(a => !a.custom && !a.idFromFollowUpTaskAppFormAnswer).map(x => x.idFromMaster);
        // Acciones por respuestas seleccionadas
        const selectedSpecialActionAnswerIds = res.taskDtos.filter(a => a.idFromFollowUpTaskAppFormAnswer).map(x => x.idFromFollowUpTaskAppFormAnswer);
        // Acciones opcionales seleccionadas
        const selectedOptionalActionAnswerIds = res.taskDtos.filter(a => a.idFromOptional).map(x => x.idFromOptional);
        // Acciones opcionales seleccionadas
        const selectedPrevCustomActionAnswerIds = res.taskDtos.filter(a => a.custom && a.canBeReUsed).map(x => x.description);

        // Marking as selected default actions
        this.evaluationResult$.getValue().tracingActions.filter(x => x.idFromMaster).forEach(x => {
          x.activeReviewed = selectedDefaultActionIds.includes(x.idFromMaster);
          if (x.activeReviewed) {
            const task = res.taskDtos.find(t => t.idFromMaster === x.idFromMaster);
            x.reUseTask = task.reUseTask;
            x.rebuiltExpirationDate = task.rebuiltExpirationDate;
          }
        });
        // Marking as selected special actions
        this.evaluationResult$.getValue().tracingActions.filter(x => x.idFromFollowUpTaskAppFormAnswer).forEach(x => {
          x.activeReviewed = selectedSpecialActionAnswerIds.includes(x.idFromFollowUpTaskAppFormAnswer)
          if (x.activeReviewed) {
            const task = res.taskDtos.find(t => t.idFromFollowUpTaskAppFormAnswer === x.idFromFollowUpTaskAppFormAnswer);
            x.reUseTask = task.reUseTask;
            x.rebuiltExpirationDate = task.rebuiltExpirationDate;
          }
        });
        // Marking as selected optional actions
        this.evaluationResult$.getValue().tracingActions.filter(x => x.idFromOptional).forEach(x => {
          x.activeReviewed = selectedOptionalActionAnswerIds.includes(x.idFromOptional)
          if (x.activeReviewed) {
            const task = res.taskDtos.find(t => t.idFromOptional === x.idFromOptional);
            x.reUseTask = task.reUseTask;
            x.rebuiltExpirationDate = task.rebuiltExpirationDate;
          }
        });
        // Marking as selected prev custom actions
        this.evaluationResult$.getValue().tracingActions.filter(x => x.custom).forEach(x => {
          x.active = selectedPrevCustomActionAnswerIds.includes(x.description)
          if (x.active) {
            const task = res.taskDtos.find(t => t.custom && t.description === x.description);
            x.reUseTask = task.reUseTask;
            x.rebuiltExpirationDate = task.rebuiltExpirationDate;
          }
        });

        this.recommendedActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>(this.evaluationResult$.getValue().tracingActions.filter(a => a.idFromMaster));
        this.specialActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>(this.evaluationResult$.getValue().tracingActions.filter(a => a.idFromFollowUpTaskAppFormAnswer));
        this.customActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>(res.taskDtos.filter(a => a.custom && !a.canBeReUsed));
        this.prevCustomActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>(this.evaluationResult$.getValue().tracingActions.filter(a => a.custom && a.canBeReUsed));
        this.optionalActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>(this.evaluationResult$.getValue().tracingActions.filter(a => a.idFromOptional));
      },
        error => {
          this.router.navigate(['/']);
        });
    }
    else {
      this._evaluationService.getEvaluationResult(thirdPartyEvaluationId).subscribe(res => {
        // Se establecen por defecto el reutilizar y reestablecer el vencimiento
        res.tracingActions.forEach(action => {
          if (action.canBeReUsed) {
            action.rebuiltExpirationDate = true;
            action.reUseTask = true;
          }
        });

        this.evaluationResult$.next(res);

        this.nextAction = this.evaluationResult$.getValue().workFlowAction;

        this.recommendedActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>(this.evaluationResult$.getValue().tracingActions.filter(a => a.idFromMaster));
        this.specialActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>(this.evaluationResult$.getValue().tracingActions.filter(a => a.idFromFollowUpTaskAppFormAnswer));
        this.prevCustomActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>(this.evaluationResult$.getValue().tracingActions.filter(a => a.custom && a.canBeReUsed));
        this.customActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>(this.evaluationResult$.getValue().tracingActions.filter(a => a.custom && !a.canBeReUsed));
        this.optionalActionsDataSource = new MatTableDataSource<ThirdPartyTracingActionListItem>(this.evaluationResult$.getValue().tracingActions.filter(a => a.idFromOptional));
      }, error => {
        this.router.navigate(['/']);
      });
    }

    this.editLocked$.subscribe(x => {
      this.createFormGroup(x);
    });

    this.createFormGroup(this.editLocked$.getValue());
  }

  createFormGroup(editLocked: boolean): void {
    editLocked = editLocked || false;

    let form = {
      changesObservation: [{ value: this.userChangesReason, disabled: editLocked || this.review },
      [Validators.required,
      Validators.maxLength(this.maxInputLength),
      Validators.minLength(this.minInputLength),
        ValidateBlankSpaces]]
    };
    this.scoreResultFormGroup = this.fb.group(form);
  }

  ngAfterViewInit(): void {
  }

  viewSpecialTaskDetail(element: ThirdPartyTracingActionListItem) {
    const dialogRef = this.dialog.open(SpecialTaskDetailDialogComponent, {
      width: '70vw',
      maxHeight: '80vh',
      data: {
        task: element,
        evaluationId: this.evaluationResult$.getValue().evaluationId
      }
    });

    dialogRef.afterClosed().subscribe({
      next: res => res
    });
  }

  openLockDialog(): void {
    if (this.editLocked$.getValue()) {
      const dialogRef = this.dialog.open(LockDialogComponent, {
        width: '250px'
      });

      dialogRef.afterClosed().subscribe((result: boolean) => {
        this.editLocked$.next(!result);
      });
    }
    else {
      this.editLocked$.next(true);
    }
  }

  /* #region Custom Tracing Actions CRUD */
  openAddTracingActionDialog(_customTracingActionTable: MatTable<any>): void {
    const dialogRef = this.dialog.open(AddtaskDialogComponent, {
      width: '40%',
      panelClass: 'dialogMobile'
    });

    dialogRef.afterClosed().subscribe((result: ThirdPartyTracingActionListItem) => {
      if (result == null) {
        return;
      }

      this.customActionsDataSource.data.push(result);
      _customTracingActionTable.renderRows();
    });
  }

  deleteCustomTracingAction(id: number | Guid): void {
    this.dialog.open(ConfirmationDialogComponent, {
      width: '300px',
      data: '',
    }).afterClosed().subscribe(confirmation => {
      if (confirmation) {
        this.customActionsDataSource.data = this.customActionsDataSource.data.filter(x => x.id != id);
        this.customTracingActionTable.renderRows();
      }
    });
  }

  editCustomTracingAction(id: number | Guid): void {
    var tracingAction: ThirdPartyTracingActionListItem = this.customActionsDataSource.data.find(x => x.id == id);

    const dialogRef = this.dialog.open(AddtaskDialogComponent, {
      width: '40%',
      data: tracingAction,
      panelClass: 'dialogMobile'
    });

    dialogRef.afterClosed().subscribe((result: ThirdPartyTracingActionListItem) => {
      if (result == null) {
        return;
      }

      var dataIndex = this.customActionsDataSource.data.findIndex(x => x.id === result.id);
      this.customActionsDataSource.data[dataIndex] = result;
      this.customTracingActionTable.renderRows();
    });
  }

  /* #endregion */

  /* #region User selection methods */
  isIndeterminateSelection(): boolean {
    var activeStateCount = this.recommendedActionsDataSource.data.filter(x => x.active).length;
    var setLength = this.recommendedActionsDataSource.data.length;

    return setLength > 0 && (0 < activeStateCount && activeStateCount < setLength);
  }

  getDeterminateSelectionState(): boolean {
    var activeStateCount = this.recommendedActionsDataSource.data.filter(x => x.active).length;
    var setLength = this.recommendedActionsDataSource.data.length;

    return setLength > 0 && (activeStateCount == setLength);
  }

  toogleAllSelection(indeterminate: boolean): void {
    var activeStateCount = this.recommendedActionsDataSource.data.filter(x => x.active).length;
    var inactiveStateCount = this.recommendedActionsDataSource.data.filter(x => !x.active).length;

    if (indeterminate) {
      this.recommendedActionsDataSource.data.forEach(x => x.active = (activeStateCount >= inactiveStateCount));
    }
    else {
      this.recommendedActionsDataSource.data.forEach(x => x.active = !x.active);
    }
  }
  /* #endregion */

  /* #region Review user selection methods */
  isIndeterminateReviewedSelection(): boolean {
    var activeStateCount = this.recommendedActionsDataSource.data.filter(x => x.activeReviewed).length;
    var setLength = this.recommendedActionsDataSource.data.length;

    return setLength > 0 && (0 < activeStateCount && activeStateCount < setLength);
  }

  getDeterminateReviewedSelectionState(): boolean {
    var activeStateCount = this.recommendedActionsDataSource.data.filter(x => x.activeReviewed).length;
    var setLength = this.recommendedActionsDataSource.data.length;

    return setLength > 0 && (activeStateCount == setLength);
  }

  toogleAllReviewedSelection(indeterminate: boolean): void {
    var activeStateCount = this.recommendedActionsDataSource.data.filter(x => x.activeReviewed).length;
    var inactiveStateCount = this.recommendedActionsDataSource.data.filter(x => !x.activeReviewed).length;

    if (indeterminate) {
      this.recommendedActionsDataSource.data.forEach(x => x.activeReviewed = (activeStateCount >= inactiveStateCount));
    }
    else {
      this.recommendedActionsDataSource.data.forEach(x => x.activeReviewed = !x.activeReviewed);
    }
  }
  /* #endregion */

  confirmEvaluationResult(): void {
    let tracingActions = this.recommendedActionsDataSource.data
      .concat(this.prevCustomActionsDataSource.data)
      .concat(this.customActionsDataSource.data)
      .concat(this.optionalActionsDataSource.data)
      .concat(this.specialActionsDataSource.data)
      .map(task => {
        // If the task is custom don't check for activeReviewed
        if (task.custom) {
          if (!task.active) {
            task.reUseTask = false;
          task.rebuiltExpirationDate = false;
          }
        } else if ((this.review && !task.activeReviewed) || (!this.review && !task.active)) {
          task.reUseTask = false;
          task.rebuiltExpirationDate = false;
        }
        return task;
      });

    var evaluationConfirmation: ThirdPartyEvaluationConfirmation = {
      tenantId: 0,
      thirdPartyId: this.evaluationResult$.getValue().thridPartyId,
      stageId: this.evaluationResult$.getValue().currentStageId,
      thirdPartyEvaluationId: this.evaluationResult$.getValue().evaluationId,
      tracingActions: tracingActions,
      workFlowAction: this.nextAction,
      formObservation: this.scoreResultFormGroup.get('changesObservation').value
    };
    // Elimino los IDs de los tracing Actions
    evaluationConfirmation.tracingActions.forEach(x => x.id = null);

    if (!this.review) {
      this._evaluationService.confirmEvaluationResult(evaluationConfirmation).subscribe(res => {
        this.trackConfirmationResponse(res);
      }, error => {
        console.log(error)
      });
    }
    else {
      // Establezco los valores de revisión como definitivos
      evaluationConfirmation.tracingActions.forEach(x => {
        if (!x.custom) {
          x.active = x.activeReviewed;
        }
      })

      this._evaluationService.confirmEvaluationApprovementRequest(evaluationConfirmation).subscribe(res => {
        this.trackConfirmationResponse(res);
      });
    }

    this.pageLoading$.next(true);
  }

  private trackConfirmationResponse(res: ThirdPartyEvaluationStatus): void {
    if (res.isPendingOfAproval) {
      this._translateService
        .get('SCORE_PAGE.NOTIFICATIONS.APPROVEMENT_REQUEST_SENT_REVIEWER')
        .subscribe(msg => this._notificationService.openSnackBar(msg));
      this.router.navigate(['/thirdparties/list']);
    }
    else if (res.isPendingOfExternalUserResponse) {
      this._translateService
        .get('SCORE_PAGE.NOTIFICATIONS.APPROVEMENT_REQUEST_SENT_THIRDPARTY')
        .subscribe(msg => this._notificationService.openSnackBar(msg));
      this.router.navigate(['/thirdparties/list']);
    }
    else if (res.isPendingOfSendDueDiligenceRequest) {
      if (this._authService.isDueDiligenceClient) {
        this._dueDiligenceService.getRequestByEvaluationId(res.thirdPartyEvaluationId).subscribe({
          next: res => {
            this._notificationService
              .openSnackBar(this._translateService.instant('SCORE_PAGE.NOTIFICATIONS.DUE_DILIGENCE_REQUEST_CREATED'));
            //TODO: VALIDAR SI EL USUARIO TIENE PERMISOS PARA ACCEDER A ESTA URL
            if(this._authService.userHasPermission(PermissionType.DueDiligenceClient) || 
                this._authService.userHasPermission(PermissionType.DueDiligenceProvider)) {
              this.router.navigate([`/due-diligence/request-form/${res.id}`]);
            } else {
              this.router.navigate(['/thirdparties/list']);
            }
          }
        });
      }
      else {
        this._notificationService
          .openSnackBar(this._translateService.instant('SCORE_PAGE.NOTIFICATIONS.DUE_DILIGENCE_REQUEST_CREATED_AND_PENDING'));
        this.router.navigate(['/thirdparties/list']);
      }
    }
    else if (res.rejectDate != null) {
      this._translateService
        .get('SCORE_PAGE.NOTIFICATIONS.EVALUATION_REJECTED')
        .subscribe(msg => this._notificationService.openSnackBar(msg));
      this.router.navigate(['/thirdparties/list']);
    }
    else if (res.finalizeDate != null) {
      this._translateService
        .get('SCORE_PAGE.NOTIFICATIONS.EVALUATION_FINALIZED')
        .subscribe(msg => this._notificationService.openSnackBar(msg));
      this.router.navigate(['/thirdparties/list']);
    }
    else if (this.review) {
      this._translateService
        .get('SCORE_PAGE.NOTIFICATIONS.APPROVEMENT_REQUEST_CONFIRMED')
        .subscribe(msg => this._notificationService.openSnackBar(msg));
      this.router.navigate(['/evaluations/tray']);
    }
    else {
      this._translateService
        .get('SCORE_PAGE.NOTIFICATIONS.NEXT_STAGE_STARTED')
        .subscribe(msg => this._notificationService.openSnackBar(msg));
      this.router.navigate([`/thirdparties/evaluation/continue/${res.thirdPartyEvaluationId}`]);
    }
  }

  cancelEvaluationResult(): void {
    if (!this.review) {
      this.router.navigate([`/thirdparties/detail/${this.evaluationResult$.getValue().thridPartyId}`]);
    }
    else {
      this.router.navigate([`/evaluations/tray`]);
    }
  }

  openPrevTaskDetail(task: ThirdPartyTracingActionListItem) {
    const isThirdPartyTask = task.followUpResponseEnum === FollowUpResponseEnum.ThirdPartyTask;
    if (task.iterativeTasks && task.iterativeTasks.length) {
      const dialog = this.dialog.open(IterativeTaskComponent, {
        data: {
          tasks: task.iterativeTasks,
          readOnly: true,
        },
        maxHeight: '100vh',
        width: '90vw',
      });
      const instance = dialog.componentInstance;
      instance.actionClicked.subscribe((subtask) => {
        const dialogData = {
          id: subtask.tracingPlanTaskId,
          subId: subtask.tracingPlanSubTaskId,
          readOnly: true,
        };

        // Si es una tarea de tercero no se debe llamar al subtaskId
        if (isThirdPartyTask) {
          dialogData.id = dialogData.subId;
          delete dialogData.subId;
        }
        this.dialog.open(TracingPlanTaskComponent, {
          data: dialogData,
          maxHeight: '100vh',
          width: '90vw',
        });
      });
    } else {
      this.dialog.open(TracingPlanTaskComponent, {
        data: {
          id: task.idFromPreviousTask,
          readOnly: true,
        },
        maxHeight: '100vh',
        width: '90vw',
      })
    }
  }
}
